/**
 * A holding pen for inspection photos that have not reached the server yet.
 *
 * A driver walks around a truck in a yard where the signal comes and goes.
 * Every photo they take lands here first, as a blob in IndexedDB, and goes up
 * from here in order, one at a time. A photo that will not go up stays put and
 * is tried again when the phone comes back on, on a timer, and when the screen
 * opens again. Nothing in here throws at the screen: a phone with no storage
 * still takes photos, it just keeps them in memory until the app is closed.
 */

import { newId } from '@relay-towing/ui';
import type {
  InspectionPhotoSource,
  UploadedPhoto,
} from '@/api/truckInspections';

const DB_NAME = 'relay_towing_inspection_photos';
const STORE = 'photos';
const RETRY_MS = 30_000;

export interface OutboxItem {
  localId: string;
  /** The run's clientSubmissionId. */
  runId: string;
  /** The checklist question the photo answers. */
  itemId: string;
  blob: Blob;
  name: string;
  type: string;
  lastModified: number;
  createdAt: number;
  /**
   * What the driver opened to get this photo: the camera, or the file picker.
   * Missing on items queued by the build before the choice existed, which were
   * all picked from the roll.
   */
  source?: InspectionPhotoSource;
}

export interface Uploaded {
  localId: string;
  runId: string;
  itemId: string;
  url: string;
  source: InspectionPhotoSource;
  takenAt?: string;
  latitude?: number;
  longitude?: number;
}

export type Upload = (file: File) => Promise<UploadedPhoto>;

interface Handlers {
  upload: Upload;
  onUploaded: (done: Uploaded) => void;
  /** A photo the server refused outright. It is dropped, not retried. */
  onRefused?: (item: OutboxItem) => void;
  /** Only this run's photos are sent. Others wait for their own screen. */
  runId?: string;
}

interface Store {
  put(item: OutboxItem): Promise<void>;
  remove(localId: string): Promise<void>;
  all(): Promise<OutboxItem[]>;
}

// --------------------------------------------------------------- the stores

const memory = new Map<string, OutboxItem>();
const memoryStore: Store = {
  put: async (item) => {
    memory.set(item.localId, item);
  },
  remove: async (localId) => {
    memory.delete(localId);
  },
  all: async () => Array.from(memory.values()),
};

let warned = false;
const warnOnce = (why: unknown) => {
  if (warned) return;
  warned = true;
  console.warn(
    'Photo storage is not available on this phone. Photos taken now are kept in memory until the app is closed.',
    why
  );
};

const request = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE, { keyPath: 'localId' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('blocked'));
  });

const withStore = async <T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
  const db = await openDb();
  try {
    return await request(run(db.transaction(STORE, mode).objectStore(STORE)));
  } finally {
    db.close();
  }
};

const dbStore: Store = {
  put: (item) => withStore('readwrite', (s) => s.put(item)).then(() => {}),
  remove: (localId) =>
    withStore('readwrite', (s) => s.delete(localId)).then(() => {}),
  all: () =>
    withStore('readonly', (s) => s.getAll() as IDBRequest<OutboxItem[]>),
};

let store: Store | null = null;

/**
 * The store to use, decided once. IndexedDB is missing in some private modes
 * and throws when the disk is full, and either way the answer is the same:
 * keep going in memory and say so once.
 */
const pickStore = (): Store => {
  if (store) return store;
  if (typeof indexedDB === 'undefined') {
    warnOnce('indexedDB is undefined');
    store = memoryStore;
  } else {
    store = dbStore;
  }
  return store;
};

/** Runs a store call and falls back to memory the first time the disk fails. */
const safely = async <T>(
  call: (s: Store) => Promise<T>,
  fallback: T
): Promise<T> => {
  try {
    return await call(pickStore());
  } catch (error) {
    if (store !== memoryStore) {
      warnOnce(error);
      store = memoryStore;
      try {
        return await call(memoryStore);
      } catch {
        return fallback;
      }
    }
    return fallback;
  }
};

const byAge = (a: OutboxItem, b: OutboxItem) => a.createdAt - b.createdAt;

/** Everything still waiting, oldest first, for one run or for all of them. */
const pending = async (runId?: string): Promise<OutboxItem[]> => {
  const all = await safely((s) => s.all(), [] as OutboxItem[]);
  return all.filter((item) => !runId || item.runId === runId).sort(byAge);
};

// ---------------------------------------------------------------- the queue

export const enqueue = async (
  runId: string,
  itemId: string,
  file: File,
  source: InspectionPhotoSource
): Promise<string> => {
  const item: OutboxItem = {
    localId: newId(),
    runId,
    itemId,
    blob: file,
    name: file.name,
    type: file.type,
    lastModified: file.lastModified,
    createdAt: Date.now(),
    source,
  };
  await safely((s) => s.put(item), undefined);
  kick();
  return item.localId;
};

export const remove = (localId: string): Promise<void> =>
  safely((s) => s.remove(localId), undefined);

export const listForRun = (runId: string): Promise<OutboxItem[]> =>
  pending(runId);

export const removeForRun = async (runId: string): Promise<void> => {
  const items = await listForRun(runId);
  for (const item of items) await remove(item.localId);
};

export const toFile = (item: OutboxItem): File =>
  new File([item.blob], item.name, {
    type: item.type,
    lastModified: item.lastModified,
  });

/**
 * A 4xx the server will give again no matter how many times we ask. Anything
 * else, including no response at all, is the network and gets another go.
 */
const refused = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return (
    typeof status === 'number' &&
    status >= 400 &&
    status < 500 &&
    status !== 401 &&
    status !== 408 &&
    status !== 429
  );
};

let draining: Promise<Uploaded[]> | null = null;

/**
 * Sends every pending photo, oldest first, one at a time. The first photo the
 * network drops ends the pass and the rest wait for the next one. Never
 * throws; a bad pass just sends nothing.
 */
export const drain = (
  upload: Upload,
  options: Pick<Handlers, 'runId' | 'onRefused'> = {}
): Promise<Uploaded[]> => {
  // A second call while a pass is running waits for that pass but gets an
  // empty list. Handing it the same results made every caller report each
  // photo, so one picture taken offline showed up two or three times once the
  // phone came back on.
  if (draining) return draining.then(() => []);
  draining = (async () => {
    const done: Uploaded[] = [];
    try {
      let queue = await pending(options.runId);
      while (queue.length) {
        let stopped = false;
        for (const item of queue) {
          try {
            const uploaded = await upload(toFile(item));
            await remove(item.localId);
            done.push({
              localId: item.localId,
              runId: item.runId,
              itemId: item.itemId,
              url: uploaded.url,
              source: item.source ?? 'UPLOAD',
              takenAt: uploaded.takenAt,
              latitude: uploaded.latitude,
              longitude: uploaded.longitude,
            });
          } catch (error) {
            if (refused(error)) {
              await remove(item.localId);
              options.onRefused?.(item);
              continue;
            }
            stopped = true;
            break;
          }
        }
        if (stopped) break;
        // A photo taken while that pass was in the air is not in the queue it
        // is walking. Re-reading the store sends it now rather than leaving it
        // to the 30-second timer, which is the normal case for three photos
        // taken one after another on the same question.
        queue = await pending(options.runId);
      }
    } catch {
      // Storage went away mid-pass. The next pass starts over.
    }
    return done;
  })().finally(() => {
    draining = null;
  });
  return draining;
};

/** Forgets the chosen store and the memory copy. Tests only. */
export const resetForTests = () => {
  store = null;
  warned = false;
  memory.clear();
  handlers = null;
  stopTimer();
  draining = null;
};

// ------------------------------------------------------------- the triggers

let handlers: Handlers | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

const stopTimer = () => {
  if (timer) clearTimeout(timer);
  timer = null;
};

/** One pass now, then another in 30 seconds if anything is still waiting. */
const kick = () => {
  const current = handlers;
  if (!current) return;
  stopTimer();
  void drain(current.upload, current).then(async (done) => {
    // The screen may have gone while the photo was in the air.
    if (handlers !== current) return;
    done.forEach(current.onUploaded);
    const left = current.runId
      ? await listForRun(current.runId)
      : await safely((s) => s.all(), [] as OutboxItem[]);
    if (left.length && handlers === current && !timer)
      timer = setTimeout(kick, RETRY_MS);
  });
};

/**
 * Wires the queue to a screen. Drains at once, when the phone comes back
 * online, and every 30 seconds while anything is pending. Returns the undo.
 */
export const connect = (next: Handlers): (() => void) => {
  handlers = next;
  const onOnline = () => kick();
  window.addEventListener('online', onOnline);
  kick();
  return () => {
    window.removeEventListener('online', onOnline);
    if (handlers === next) {
      handlers = null;
      stopTimer();
    }
  };
};
